import { makeAutoObservable, runInAction } from 'mobx';
import { enableStaticRendering } from 'mobx-react-lite';
import Router from 'next/router';
import { removeTabRelatedChatSessionId } from '../../clients/chat/connection';
import { ChatEntity } from '../../components/plugins/chat/types';
import { CustomizedDetailedUser } from '../../hooks/useUserById';
import {
    NewTicket,
    Organization,
    OrganizationTab,
    PluginTab,
    SimpleTicketTab,
    Tab,
    TabType,
    TicketTab,
    UserTab,
    channelTypeMap,
    statusOptions,
    tabTypeMap
} from '../../model/types';
import { getTabListInfo } from '../../services/tab';
import { generateTicketFormName, removeFormRelatedStorageItems } from '../../utils/forms/formHelpers';
import {
    getMaskedEmailForUI,
    isRelatedTabAlreadyOpen,
    isSSR,
    nonNullishValue,
    nullishValue,
    pipe,
    replaceItemAtIndex
} from '../../utils/helpers';
import {
    isCustomerPage,
    isNewTicketPage,
    isOrganizationPage,
    isPagePluginPage,
    isTicketsPage
} from '../../utils/routeHelper';
import { StorageHelperKeys, getSessionStorageItem, setSessionStorageItem } from '../../utils/storageHelpers';
import getStore from '../../utils/store/getStore';
import {
    getTicketDescription,
    getTicketFullStatus,
    getTicketRequester,
    getTicketSubject
} from '../../utils/ticketHelper';

enableStaticRendering(isSSR());

const clearDebugHash =
    (debugHash = '#debug') =>
    (url: string): string =>
        url?.replace(debugHash, '');
export const getTicketKeyFromUrl = (url: string): string => url.split('/').find((query) => query.includes('TICKET-'));
export const getSafeTicketKeyFromUrl = (url: string, debugHash?: string): string =>
    pipe(getTicketKeyFromUrl, clearDebugHash(debugHash))(url);
export const getNewTicketKeyFromUrl = (url: string): string => `newTicket${url.split('/').pop()}`;

export const getUserIdFromUrl = (url: string): string => url.split('/')[2];
export const getOrganizationIdFromUrl = (url: string): string => url.split('/')[2];
export const getPluginIdFromUrl = (url: string): string => url.split('/').pop();

export const getSelectedTabKeyFromUrl = (url: string): string => {
    if (isNewTicketPage(url)) return getNewTicketKeyFromUrl(url);
    if (isTicketsPage(url)) return getTicketKeyFromUrl(url);
    if (isCustomerPage(url)) return `USER-${getUserIdFromUrl(url)}`;
    if (isOrganizationPage(url)) return `ORGANIZATION-${getOrganizationIdFromUrl(url)}`;
    if (isPagePluginPage(url)) return `PLUGIN-${getPluginIdFromUrl(url)}`;

    return null;
};

export type TabDataSource<T extends TabType = TabType> = T extends typeof tabTypeMap.TICKET
    ? NewTicket
    : T extends typeof tabTypeMap.USER
    ? CustomizedDetailedUser
    : T extends typeof tabTypeMap.ORGANIZATION
    ? Organization
    : never;
export const generateTab: <T extends TabType = TabType>(dataSource: TabDataSource<T>, type: T, url: string) => Tab = (
    dataSource,
    type,
    url
) => {
    if (dataSource) {
        if (type === tabTypeMap.TICKET && 'fieldMap' in dataSource) {
            return {
                channel: dataSource.channel,
                description: getTicketDescription(dataSource) ?? 'No description available',
                key: getSelectedTabKeyFromUrl(url),
                requester: getMaskedEmailForUI(getTicketRequester(dataSource)),
                status: getTicketFullStatus(dataSource),
                subject: getTicketSubject(dataSource),
                type: tabTypeMap.TICKET,
                url,
                ...(dataSource.channel === channelTypeMap.CHAT
                    ? { unreadMessagesCount: 0, chatId: null, chatSessionId: null }
                    : {})
            } as TicketTab;
        }
        if (type === tabTypeMap.USER && 'primaryEmail' in dataSource) {
            return {
                email: dataSource.primaryEmail?.email,
                fullName: dataSource.fullName || dataSource.primaryEmail?.email,
                key: getSelectedTabKeyFromUrl(url),
                phone: dataSource.primaryPhone?.phone,
                type: tabTypeMap.USER,
                url: url,
                role: dataSource.role?.authority
            } as UserTab;
        }
        if (type === tabTypeMap.ORGANIZATION && 'name' in dataSource) {
            return {
                name: dataSource.name,
                key: getSelectedTabKeyFromUrl(url),
                type: tabTypeMap.ORGANIZATION,
                url: url
            } as OrganizationTab;
        }
    }

    if (type === tabTypeMap.PLUGIN) {
        return {
            key: getSelectedTabKeyFromUrl(url),
            type: tabTypeMap.PLUGIN,
            url: url,
            pluginId: getPluginIdFromUrl(url)
        } as PluginTab;
    }

    // TODO: tabs, implement DealTab
};

const defaultNewTicketTabValue: Omit<SimpleTicketTab, 'key' | 'url'> = {
    channel: channelTypeMap.WEB,
    description: 'New Ticket',
    requester: '',
    status: { id: 1, name: statusOptions.New },
    subject: 'New Ticket',
    type: tabTypeMap.TICKET
};

export const createNewTicketTab = (key: string): SimpleTicketTab => ({
    ...defaultNewTicketTabValue,
    key,
    url: `/tickets/new/${key}`
});

export const generateTicketFormNameForStorage = (targetKey: string): string => {
    if (targetKey.includes('newTicket'))
        return generateTicketFormName('/tickets/new/[no]', { no: targetKey.replace('newTicket', '') });

    return generateTicketFormName('/tickets/[ticketKey]', { ticketKey: targetKey });
};

const setTabsOnSessionStorage = (tabs: Tab[], replaceFlag = false): void => {
    const cleanedTabKeyList = tabs.filter(({ key }) => !key.startsWith('newTicket')).map(({ key }) => key);

    const tabKeyListOnStorage = !replaceFlag
        ? (getSessionStorageItem(StorageHelperKeys.OPEN_TAB_KEYS) ?? '').split(',').filter((v) => v)
        : [];

    const newTabKeyListValue = [...new Set([...tabKeyListOnStorage, ...cleanedTabKeyList])].join(',');

    setSessionStorageItem(StorageHelperKeys.OPEN_TAB_KEYS, newTabKeyListValue);
};

export const createURLFromTab = (tab: Tab): string => {
    if (tab?.key.startsWith('newTicket')) {
        const [, no] = tab.key.split('newTicket');
        return `/tickets/new/${no}`;
    }

    const url = tab.url ?? '';
    return url;
};
export const TAB_LIMIT = 10;

export class TabStore {
    tabs: Tab[];
    selectedTabKey: string;
    newTabIndex: number;
    constructor() {
        this.tabs = [];
        this.selectedTabKey = null;
        this.newTabIndex = 1;
        // Call it here
        makeAutoObservable(this);
    }

    /**
     * this function is called when a ticket page is rendered, and update tabs by adding new one or sync with existing one
     * @param url should always be a ticket|customer|organization|deal|plugin page.
     * @param tabDataSource if url is not for a new ticket then tabDataSource is always defined and has the info
     */
    updateTicketTabs = (tabDataSource: TabDataSource, hydrationFlag = false): void => {
        const url = Router.asPath;

        const tabKey = getSelectedTabKeyFromUrl(url);
        if (!tabKey) return;

        const isAtNewTicketPage = isNewTicketPage(url);
        const isAtPagePluginPage = isPagePluginPage(url);

        const relatedTabAlreadyOpen = isRelatedTabAlreadyOpen(this.tabs, tabKey);

        if ((isAtNewTicketPage || isAtPagePluginPage || hydrationFlag) && relatedTabAlreadyOpen) return;

        const tabType: TabType = tabKey.startsWith(tabTypeMap.TICKET)
            ? tabTypeMap.TICKET
            : tabKey.startsWith(tabTypeMap.USER)
            ? tabTypeMap.USER
            : tabKey.startsWith(tabTypeMap.ORGANIZATION)
            ? tabTypeMap.ORGANIZATION
            : tabKey.startsWith(tabTypeMap.PLUGIN)
            ? tabTypeMap.PLUGIN
            : undefined;

        const newTab = isAtNewTicketPage ? createNewTicketTab(tabKey) : generateTab(tabDataSource, tabType, url);

        if (!newTab) return;

        if (!relatedTabAlreadyOpen) {
            this.tabs = [...this.tabs, newTab];
            setTabsOnSessionStorage(this.tabs);
            // tabType === tabTypeMap.TICKET && fireTicketLifecycleEvent(TicketLifecycleEventName.TicketOpened, tabKey);
            return; // When a tab is created there's no need to update it again
        }

        if (!isAtNewTicketPage || !isAtPagePluginPage) {
            // currentTicket param is effectively comes from Grispi API
            // We need to update ticket data in the store with the new ticket data (currentTicket)
            const currentTabIndex = this.tabs.findIndex(({ key }) => key === newTab.key);
            const currentTab = this.tabs[currentTabIndex];

            const isChatTicketTab = newTab && 'unreadMessagesCount' in newTab;
            const isCurrentTabChatTicketTab = currentTab && 'unreadMessagesCount' in currentTab;
            if (isChatTicketTab && isCurrentTabChatTicketTab) {
                // to pass last message as description
                newTab.description = currentTab.description;
                newTab.unreadMessagesCount = currentTab.unreadMessagesCount;
                newTab.chatId = currentTab.chatId;
                newTab.chatSessionId = currentTab.chatSessionId;
            }
            const newTabList = replaceItemAtIndex(this.tabs, currentTabIndex, newTab);

            this.tabs = newTabList;
        }
        setTabsOnSessionStorage(this.tabs);
    };

    updateTabUrlByUrl = (url: string) => {
        const tabKey = getSelectedTabKeyFromUrl(url);
        if (!tabKey) return;
        this.tabs = this.tabs.map((tab) => {
            if (tab.key === tabKey) {
                return { ...tab, url };
            } else {
                return tab;
            }
        });
    };

    updateChatTicketTab = ({
        propertyKey,
        value,
        tabKey,
        increaseCountFlag
    }: {
        propertyKey: 'chatId' | 'chatSessionId' | 'description';
        value: string;
        tabKey: string;
        increaseCountFlag: boolean;
    }): void => {
        if (value && tabKey) {
            const currentChatTicketTabIndex = this.tabs.findIndex(({ key }) => key === tabKey);
            const currentChatTicketTab = this.tabs[currentChatTicketTabIndex];
            const isCurrentTabChatTicketTab = currentChatTicketTab && 'chatId' in currentChatTicketTab;

            if (isCurrentTabChatTicketTab) {
                const updatedChatTicketTab = { ...currentChatTicketTab };
                updatedChatTicketTab[propertyKey] = value;

                if (increaseCountFlag) {
                    updatedChatTicketTab.unreadMessagesCount = currentChatTicketTab.unreadMessagesCount + 1;
                } else {
                    updatedChatTicketTab.unreadMessagesCount = 0;
                }

                if (propertyKey === 'chatSessionId' && currentChatTicketTab.chatSessionId !== value) {
                    if (!nullishValue(value)) {
                        // new connection
                        updatedChatTicketTab.unreadMessagesCount = 0;
                    }
                    // unsubscription
                }

                const newTabList = replaceItemAtIndex(this.tabs, currentChatTicketTabIndex, updatedChatTicketTab);
                this.tabs = newTabList;
                setTabsOnSessionStorage(this.tabs);
                return;
            }
        }
    };

    resetMessageCount = (tabKey: string): void => {
        if (tabKey) {
            const currentChatTicketTabIndex = this.tabs.findIndex(({ key }) => key === tabKey);
            const currentChatTicketTab = this.tabs[currentChatTicketTabIndex];
            const isCurrentTabChatTicketTab = currentChatTicketTab && 'chatId' in currentChatTicketTab;
            if (isCurrentTabChatTicketTab) {
                const updatedChatTicketTab = { ...currentChatTicketTab };
                updatedChatTicketTab.unreadMessagesCount = 0;
                const newTabList = replaceItemAtIndex(this.tabs, currentChatTicketTabIndex, updatedChatTicketTab);
                this.tabs = newTabList;
                setTabsOnSessionStorage(this.tabs);
                return;
            }
        }
    };

    injectChatInfoToChatTicketTab = (chatEntity: ChatEntity): void => {
        if (chatEntity) {
            const targetChatTicketTabIndex = this.tabs.findIndex(({ key }) => key === chatEntity.ticketKey);
            const targetChatTicketTab = this.tabs[targetChatTicketTabIndex];
            const isTargetTabChatTicketTab = targetChatTicketTab && 'unreadMessagesCount' in targetChatTicketTab;

            if (isTargetTabChatTicketTab) {
                const updatedChatTicketTab = { ...targetChatTicketTab };
                updatedChatTicketTab.chatId = chatEntity.chatId;
                updatedChatTicketTab.chatSessionId = chatEntity.chatSessionId;

                const newTabList = replaceItemAtIndex(this.tabs, targetChatTicketTabIndex, updatedChatTicketTab);
                this.tabs = newTabList;
                setTabsOnSessionStorage(this.tabs);
                return;
            }
        }
    };

    targetUrlFactory = (tabToClose: Tab): string => {
        const selectedTabKey = getSelectedTabKeyFromUrl(Router.asPath);

        // no need for tab or route changes
        if (tabToClose?.key !== selectedTabKey) return;

        if (this.tabs.length === 0) return getStore().UtilStore.lastVisitedFilterPageUrl;

        // select the first tab
        if (this.tabs.length === 1) {
            return createURLFromTab(this.tabs.at(0));
        }

        // select automatically the most-right-placed tab
        if (tabToClose?.key === selectedTabKey) {
            return createURLFromTab(this.tabs.at(this.tabs.length - 1));
        }
    };

    closeTabAndGetNewTargetUrl = (tabKeyToClose: string): string => {
        const tabToClose = this.tabs.find((t) => t.key === tabKeyToClose);
        const isTicketTab = tabToClose?.type === tabTypeMap.TICKET;
        this.tabs = this.tabs.filter((t) => t.key !== tabKeyToClose);
        setTabsOnSessionStorage(this.tabs, true);

        if (isTicketTab) {
            const formName = generateTicketFormNameForStorage(tabKeyToClose);

            removeFormRelatedStorageItems(formName);
            // fireTicketLifecycleEvent(TicketLifecycleEventName.TicketClosed, tabKeyToClose);
            getStore().TicketFormUtilStore.resetFieldOptions(formName); // TODO: custom-field, remove after previos CustomField implementation is deprecated
            getStore().TicketFormUtilStore.resetInitialValues(formName); // TODO: custom-field, remove after previos CustomField implementation is deprecated
            getStore().MacroStore.removePlaceholderValuesOfCurrentPage();
            tabToClose.channel === 'CHAT' && removeTabRelatedChatSessionId(tabToClose?.chatSessionId);
        } else {
            removeFormRelatedStorageItems(tabKeyToClose);
        }

        return this.targetUrlFactory(tabToClose);
    };

    addNewTicketTabAndGetTargetUrl = (): string => {
        if (this.tabs.length >= TAB_LIMIT) return;
        const doesTabAlreadyExist = this.tabs.find((t) => t.key === `newTicket${this.newTabIndex}`);
        const calculatedNewTabIndex = doesTabAlreadyExist ? this.newTabIndex + 1 : this.newTabIndex;
        this.increaseNewTicketTabIndex(doesTabAlreadyExist ? 2 : 1);

        return `/tickets/new/${calculatedNewTabIndex}`;
    };

    increaseNewTicketTabIndex = (increaseBy = 1): void => {
        this.newTabIndex = this.newTabIndex + increaseBy;
    };
    getTabByTabKey = (tabKey: string): Tab => {
        return this.tabs.find((tab) => tab.key === tabKey);
    };

    // to hydrate tabs from session storage
    hydrate = async (openTabKeys?: string): Promise<void> => {
        if (nonNullishValue(openTabKeys) && openTabKeys !== '') {
            try {
                const openTabListInfo = await getTabListInfo(openTabKeys);

                // HACK: workaround for api-limitation, remove later
                openTabListInfo.forEach((tab) => {
                    if (!tab?.type) tab.type = tab.key.split('-')[0] as Tab['type'];

                    if ('channel' in tab && tab.channel === channelTypeMap.CHAT) {
                        tab.type = 'TICKET';
                        tab.chatSessionId = null;
                    }
                });
                runInAction(() => {
                    const filteredTabListInfo = openTabListInfo.filter(({ key }) => key !== this.tabs[0]?.key);
                    this.tabs = [...this.tabs, ...filteredTabListInfo];
                });
            } catch (error) {
                console.error(error);
            }
        }
    };
}
